import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';

import { ImpactBoxes } from './impact_boxes.wjs.jsx';

export class ImpactTabContainer extends React.Component {
  static propTypes = {
    docId: PropTypes.number,
  };

  getStateFromStore = () => {
    return {
      displayedDates: this.getDisplayedDates(),
      countries: this.getCountries(),
      works: this.getDocs(),
      hits: this.getHits(),
      downloads: this.getDownloads(),
      readerResearchInterests: this.getReaderResearchInterests(),
      userPercentile: this.getUserPercentile(),
      researchInterestPercentiles: this.getResearchInterestPercentiles(),
    };
  };

  getDisplayedDates = () => {
    return Aedu.ReduxStore.getState().AnalyticsDashboard.DisplayedDates;
  };

  getCountries = () => {
    if (this.props.docId) {
      const docs = Aedu.ReduxStore.getState().AnalyticsDashboard.Docs;
      const doc = _.findWhere(docs.models, { id: this.props.docId });

      return {
        meta: docs.meta,
        models: doc.countries,
      };
    } else {
      return Aedu.ReduxStore.getState().AnalyticsDashboard.Countries;
    }
  };

  getDocs = () => {
    const store = Aedu.ReduxStore.getState().AnalyticsDashboard.Docs;
    return this.props.docId
      ? this.filterStore(store, function (doc) {
          return doc.id;
        })
      : store;
  };

  getHits = () => {
    const store = Aedu.ReduxStore.getState().AnalyticsDashboard.Hits;
    return this.props.docId
      ? this.filterStore(store, function (hit) {
          return hit.page.work_id;
        })
      : store;
  };

  getDownloads = () => {
    const store = Aedu.ReduxStore.getState().AnalyticsDashboard.Downloads;
    return this.props.docId
      ? this.filterStore(store, function (download) {
          return download.work_id;
        })
      : store;
  };

  getReaderResearchInterests = () => {
    const readerInterests =
      Aedu.ReduxStore.getState().AnalyticsDashboard.ReaderResearchInterests;
    const counts = this.findModelByPage(readerInterests.perPageCounts);

    // TODO (per-slice error UI): a FAILED fetch collapses to `models: [], count: 0`
    // here — indistinguishable from "genuinely no reader research interests". This
    // tab fans out to 7 independent slices, so a single-slice failure (the incident
    // was `reader_research_interests`) renders a confidently-empty box rather than an
    // error state, unlike the whole-page NoDataTab in TwelveMonthImpactReportContainer.
    // Surfacing per-slice `meta.errorFetching` is deliberately deferred to the
    // follow-up on why reader_research_interests errors; this asymmetry is intentional
    // for now, not an oversight.
    return {
      meta: readerInterests.meta,
      models: counts ? counts.per_research_interest_counts : [],
      count: counts ? counts.research_interest_count : 0,
    };
  };

  getUserPercentile = () => {
    const percentiles =
      Aedu.ReduxStore.getState().AnalyticsDashboard.Percentiles;
    const percentile = this.findModelByPage(percentiles.models);

    return {
      meta: percentiles.meta,
      model: percentile,
    };
  };

  getResearchInterestPercentiles = () => {
    const percentiles =
      Aedu.ReduxStore.getState().AnalyticsDashboard.ResearchInterestPercentiles;
    if (this.props.docId) {
      return this.filterStore(percentiles, function (percentile) {
        return percentile.percentile && percentile.page.work_id;
      });
    } else {
      const filtered = _.filter(percentiles.models, function (percentile) {
        // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
        // eslint-disable-next-line eqeqeq
        return percentile.percentile && percentile.page.type == 'all';
      });

      return {
        meta: percentiles.meta,
        models: filtered,
      };
    }
  };

  filterStore = (store, docIdGetter) => {
    const filtered = _.filter(
      store.models,
      function (model) {
        return docIdGetter(model) === this.props.docId;
      }.bind(this),
    );

    return {
      meta: store.meta,
      models: filtered,
    };
  };

  findModelByPage = (among) => {
    let filter;
    if (this.props.docId) {
      filter = function (obj) {
        return obj.page.work_id === this.props.docId;
      }.bind(this);
    } else {
      filter = function (obj) {
        return obj.page.type === 'all';
      }.bind(this);
    }

    return _.find(among, filter);
  };

  dispatchFetches = () => {
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.Countries.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.Docs.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.Hits.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.ReaderResearchInterests.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.Downloads.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.Percentiles.fetch(),
    );
    Aedu.ReduxStore.dispatch(
      Aedu.ActionCreators.AnalyticsDashboard.ResearchInterestPercentiles.fetch(),
    );
  };

  componentWillMount() {
    this.unsubscribe = Aedu.ReduxStore.subscribe(
      function () {
        this.setState(this.getStateFromStore());
      }.bind(this),
    );

    this.dispatchFetches();
  }

  // Re-fetch only when the displayed date range changes, since that is the only
  // action that resets any slice's cache (5 of the 7 dispatched here — the two
  // percentile slices have no SET_DISPLAYED_DATES reset and stay exhausted).
  // Running dispatchFetches on every re-render (this component setState's on every
  // store change) turned a single failed fetch — whose slice doesn't latch
  // `exhausted` on error — into an unbounded refetch loop (~1 req/sec) that
  // tripped Cloudflare rate limiting.
  componentDidUpdate(prevProps, prevState) {
    if (prevState.displayedDates !== this.state.displayedDates) {
      this.dispatchFetches();
    }
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  countUniqueVisitorsAmong = (hits) => {
    return _.chain(hits)
      .groupBy(function (hit) {
        return hit.visitor.id;
      })
      .size()
      .value();
  };

  aggregateState = () => {
    return {
      counts: {
        visitor_count: this.countUniqueVisitorsAmong(this.state.hits.models),
        download_count: _.reduce(
          this.state.works.models,
          function (memo, work) {
            return work.thirtyDayDownloads + memo;
          },
          0,
        ),
        hit_count: this.state.hits.models.length,
      },

      collections: {
        countries: _.chain(this.state.countries.models)
          .filter(function (country) {
            return country.uniqueVisitors > 0;
          })
          .map(function (country) {
            return {
              country: {
                code: country.countryCode,
                countryName: country.countryName,
              },
              visitor_count: country.uniqueVisitors,
            };
          })
          .value(),
        cities: _.chain(this.state.countries.models)
          .pluck('cities')
          .flatten()
          .map(function (city) {
            return {
              city: {
                name: city.name,
              },
              visitor_count: city.uniqueVisitors,
            };
          })
          .value(),
        universities: _.chain(this.state.hits.aggregatedUniversities)
          .map(
            function (university) {
              return {
                university: {
                  name: university.display_name,
                  url: university.url,
                },
                visitor_count: university.count,
              };
            }.bind(this),
          )
          .value(),
        positions: _.chain(this.state.hits.aggregatedOccupations)
          .map(
            function (occupation) {
              return {
                position: {
                  name: occupation.name,
                },
                visitor_count: occupation.count,
              };
            }.bind(this),
          )
          .value(),
        docs: this.state.hits.aggregatedDocs,
        furthest_pages: _.chain(this.state.hits.models)
          .map(function (hit) {
            return hit.furthest_page;
          })
          .compact()
          .value(),
        traffic_sources: _.chain(this.state.hits.models)
          .groupBy(function (hit) {
            return hit.source.name || 'Direct';
          })
          .map(function (hits, name) {
            return {
              traffic_source: {
                source: {
                  name: name,
                },
              },
              hit_count: hits.length,
            };
          })
          .value(),
        searches: _.chain(this.state.hits.models)
          .filter(function (hit) {
            return hit.source && hit.source.type === 'ExternalSearch';
          })
          .groupBy(function (hit) {
            return (hit.referer && hit.referer.query) || hit.source.name;
          })
          .map(function (hits, sourceName) {
            return {
              search: {
                source: {
                  name: sourceName,
                },
              },
              hit_count: hits.length,
            };
          })
          .value(),
      },
    };
  };

  showPagesReadDisclaimer = () => {
    const allPresent = _.every(this.state.hits.models, function (hit) {
      return !hit.page.work_id || hit.furthest_page;
    });

    if (allPresent) return false;

    const anyPageReadData = _.any(this.state.hits.models, function (hit) {
      return hit.furthest_page;
    });

    return anyPageReadData;
  };

  chooseWhichBoxesToShow = () => {
    const boxSettings = {
      singleWorkMode: !!this.props.docId,
    };

    if (this.props.docId === Aedu.WorkConstants.CV_WORK_ID) {
      boxSettings.hideResearchField = true;
      boxSettings.hidePercentile = true;
    }

    return boxSettings;
  };

  state = this.getStateFromStore();

  render() {
    const aggregatedData = this.aggregateState();
    const boxSettings = this.chooseWhichBoxesToShow();

    return (
      <ImpactBoxes
        displayedDates={this.state.displayedDates}
        userPercentile={this.state.userPercentile}
        visitorCount={aggregatedData.counts.visitor_count}
        visitorCountFetching={this.state.hits.meta.fetching}
        downloadCount={aggregatedData.counts.download_count}
        downloadCountFetching={this.state.works.meta.fetching}
        hitCount={aggregatedData.counts.hit_count}
        hitCountFetching={this.state.hits.meta.fetching}
        topCountries={aggregatedData.collections.countries}
        topCountriesFetching={this.state.countries.meta.fetching}
        topCities={aggregatedData.collections.cities}
        topCitiesFetching={this.state.countries.meta.fetching}
        topUniversities={aggregatedData.collections.universities}
        topUniversitiesFetching={this.state.hits.meta.fetching}
        topPositions={aggregatedData.collections.positions}
        topPositionsFetching={this.state.hits.meta.fetching}
        topDocs={aggregatedData.collections.docs}
        topDocsFetching={this.state.hits.meta.fetching}
        // This is for single work impact only
        furthestPages={aggregatedData.collections.furthest_pages}
        furthestPagesFetching={this.state.hits.meta.fetching}
        showPagesReadDisclaimer={this.showPagesReadDisclaimer()}
        topTrafficSources={aggregatedData.collections.traffic_sources}
        topTrafficSourcesFetching={this.state.hits.meta.fetching}
        topSearches={aggregatedData.collections.searches}
        topSearchesFetching={this.state.hits.meta.fetching}
        topResearchInterestPercentiles={
          this.state.researchInterestPercentiles.models
        }
        topResearchInterestPercentilesFetching={
          this.state.researchInterestPercentiles.meta.fetching
        }
        readerResearchInterestCount={this.state.readerResearchInterests.count}
        topReaderResearchInterests={this.state.readerResearchInterests.models}
        topReaderResearchInterestsFetching={
          this.state.readerResearchInterests.meta.fetching
        }
        {...boxSettings}
      />
    );
  }
}
